import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import CustomButton from '../../../../../../shared/components/Buttons/CustomButton/CustomButton';
import DropdownMenu from '../../../../../../shared/components/Globals/DropdownMenu/DropdownMenu';
import { CLICK_ACTION, MARK_AS_INACTIVE, MARK_AS_LOST } from '../../../../../../shared/constants/tagManager';
import TagManagerService from '../../../../../../shared/services/gtm/tagManagerService';
import { TRANSLATIONS_PROSPECTS } from '../../../../../../shared/translations/prospects';

import './ProspectActionsMenu.scss';

interface IProspectActionsMenuProps {
  prospectId: number;
  onMarkAsLost: (id: number) => void;
  onMarkAsInactive: (id: number) => void;
  disabled?: boolean;
}

const VIEWPORT_MARGIN = 8;
const TRIGGER_GAP = 4;

const ProspectActionsMenu = ({
  prospectId,
  onMarkAsLost,
  onMarkAsInactive,
  disabled = false
}: IProspectActionsMenuProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const portalRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      const insideWrapper = wrapperRef.current?.contains(target);
      const insidePortal = portalRef.current?.contains(target);
      if (!insideWrapper && !insidePortal) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  useLayoutEffect(() => {
    if (!open) return;
    const trigger = triggerRef.current;
    const wrapper = portalRef.current;
    const menu = wrapper?.firstElementChild as HTMLElement | null;
    if (!trigger || !wrapper || !menu) return;

    const rect = trigger.getBoundingClientRect();
    const wrapperRect = wrapper.getBoundingClientRect();
    const menuRect = menu.getBoundingClientRect();
    const { width, height } = menuRect;
    const offsetLeft = menuRect.left - wrapperRect.left;
    const offsetTop = menuRect.top - wrapperRect.top;

    const maxLeft = Math.max(window.innerWidth - width - VIEWPORT_MARGIN, VIEWPORT_MARGIN);
    const desiredLeft = Math.min(Math.max(rect.right - width, VIEWPORT_MARGIN), maxLeft);

    const fitsBelow = window.innerHeight - rect.bottom >= height + TRIGGER_GAP + VIEWPORT_MARGIN;
    const desiredTop = fitsBelow ? rect.bottom + TRIGGER_GAP : rect.top - TRIGGER_GAP - height;

    setCoords({ top: desiredTop - offsetTop, left: desiredLeft - offsetLeft });
  }, [open]);

  const toggleOpen = () => {
    setCoords(null);
    setOpen((v) => !v);
  };

  const moreMenuItems = useMemo(
    () => [
      {
        key: 'lost',
        label: t(TRANSLATIONS_PROSPECTS.detail.actions.markAsLost),
        onClick: () => {
          onMarkAsLost(prospectId);
          setOpen(false);
          TagManagerService.pushEvent(CLICK_ACTION, MARK_AS_LOST);
        }
      },
      {
        key: 'inactive',
        label: t(TRANSLATIONS_PROSPECTS.detail.actions.markAsInactive),
        onClick: () => {
          onMarkAsInactive(prospectId);
          setOpen(false);
          TagManagerService.pushEvent(CLICK_ACTION, MARK_AS_INACTIVE);
        }
      }
    ],
    [t, prospectId, onMarkAsLost, onMarkAsInactive]
  );

  return (
    <div className="prospect-actions-menu" ref={wrapperRef}>
      <CustomButton
        ref={triggerRef}
        variant="default"
        size="s"
        aria-label="More options"
        aria-haspopup="menu"
        aria-expanded={open}
        disabled={disabled}
        onClick={toggleOpen}
      >
        ···
      </CustomButton>
      {open &&
        createPortal(
          <div
            ref={portalRef}
            className="prospect-actions-menu__portal"
            style={{
              position: 'fixed',
              top: coords?.top ?? 0,
              left: coords?.left ?? 0,
              zIndex: 9999,
              visibility: coords ? 'visible' : 'hidden'
            }}
          >
            <DropdownMenu placement="bottom" items={moreMenuItems} />
          </div>,
          document.body
        )}
    </div>
  );
};

export default ProspectActionsMenu;
